<template>
  <div class="seat-calendar-page">
    <div class="monthNavWrap">
      <button
        @click="prevMonth"
        class="monthNav"
        :disabled="isPrevMonthDisabled"
      >
        ◀︎
      </button>
      <h2>{{ monthName }} {{ year }}</h2>
      <button
        @click="nextMonth"
        class="monthNav"
        :disabled="isNextMonthDisabled"
      >
        ▶︎
      </button>
    </div>
    <div class="weekendSwc">
      <span>Weekends</span>
      <input
        type="checkbox"
        id="hide-toggle"
        v-model="hideWeekends"
        class="weekendCheck"
      />
      <label for="hide-toggle" class="weekendLabel"></label>
    </div>

    <table class="seatCalendar">
      <thead>
        <tr>
          <th class="bgBlu"></th>
          <th v-for="seat in seatLabels" :key="seat.code" class="bgBlu">
            {{ seat.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="date in filteredDays"
          :key="date.toISOString()"
          :class="{
            today: isToday(date),
            past: isPast(date),
            sunday: isSunday(date),
            saturday: isSaturday(date),
            'uk-holiday': isHoliday(date),
          }"
        >
          <td>
            <span>{{ date.getDate() }}</span>
            <span class="weekday">{{ formatWeekday(date) }}</span>
          </td>
          <td
            v-for="seat in seatLabels"
            :key="`${dateString(date)}_${seat.code}`"
            @click="openModal(date, seat.code)"
            :class="{
              disabled: hideWeekends && (isWeekend(date) || isHoliday(date)),
              unavailable: !isSeatBookable(date, seat.code),
              reserved: reservationMap[`${dateString(date)}_${seat.code}`],
              mine:
                reservationMap[`${dateString(date)}_${seat.code}`]?.user ===
                currentUserId,
            }"
          >
            <span
              v-if="reservationMap[`${dateString(date)}_${seat.code}`]"
              :class="{
                'seat-reserved': true,
                mine:
                  reservationMap[`${dateString(date)}_${seat.code}`].user ===
                  currentUserId,
                guest:
                  reservationMap[`${dateString(date)}_${seat.code}`].is_guest,
                other:
                  !reservationMap[`${dateString(date)}_${seat.code}`]
                    .is_guest &&
                  reservationMap[`${dateString(date)}_${seat.code}`].user !==
                    currentUserId,
              }"
            >
              {{
                reservationMap[`${dateString(date)}_${seat.code}`].is_guest
                  ? reservationMap[`${dateString(date)}_${seat.code}`]
                      .display_name
                  : reservationMap[`${dateString(date)}_${seat.code}`]
                      .user_username
              }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
    <ReservationModal
      v-if="showModal"
      :visible="showModal"
      :seat="modalSeat"
      :initialDate="modalDate"
      :users="users"
      :reservations="
        [].concat(
          ...Object.values(reservationMap).map((r) => [{ user: r.user }])
        )
      "
      @close="closeModal"
      @confirmed="afterConfirm"
    />
    <CancelSeatModal
      v-if="showCancelModal"
      :visible="showCancelModal"
      :reservationId="cancelReservationId"
      @close="showCancelModal = false"
      @confirmed="onCancelConfirmed"
    />
    <div v-if="loading">Loading...</div>
    <div v-if="error" class="error">{{ error }}</div>
  </div>
</template>

<script>
import api, {
  fetchSeatLabels as apiFetchSeatLabels,
  fetchHolidays,
} from "@/services/api";
import axios from "axios";
import ReservationModal from "@/components/ReservationModal.vue";
import CancelSeatModal from "@/components/CancelSeatModal.vue";

const NEW_SEAT_LAYOUT_START = "2026-06-02";
const NEW_LAYOUT_RETIRED_SEATS = ["T", "U", "V"];

export default {
  name: "DeskCalendarView",
  components: {
    ReservationModal,
    CancelSeatModal,
  },
  data() {
    return {
      month: this.getCurrentMonth(),
      seatLabels: [],
      reservationMap: {},
      loading: false,
      error: "",
      hideWeekends: false,
      holidays: [],
      users: [],
      showModal: false,
      modalDate: null,
      modalSeat: null,
      showCancelModal: false,
      cancelReservationId: null,
    };
  },
  computed: {
    currentUserId() {
      return Number(localStorage.getItem("userId"));
    },
    filteredDays() {
      return this.daysInMonth.filter((date) => {
        if (!this.hideWeekends) return true;
        return !(
          this.isSaturday(date) ||
          this.isSunday(date) ||
          this.isHoliday(date)
        );
      });
    },
    daysInMonth() {
      const [year, mon] = this.month.split("-").map(Number);
      const d = new Date(year, mon - 1, 1);
      const days = [];
      while (d.getMonth() === mon - 1) {
        days.push(new Date(d));
        d.setDate(d.getDate() + 1);
      }
      return days;
    },
    year() {
      return this.month.split("-")[0];
    },
    monthName() {
      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const m = Number(this.month.split("-")[1]);
      return monthNames[m - 1];
    },
    isPrevMonthDisabled() {
      const [y, m] = this.month.split("-").map(Number);
      const d = new Date(y, m - 2, 1);
      const now = this.$now();
      const minDisplayDate = new Date(now.getFullYear(), now.getMonth() - 3, 1); // Display 3 months ago
      return d < minDisplayDate;
    },
    isNextMonthDisabled() {
      const [y, m] = this.month.split("-").map(Number);
      const d = new Date(y, m, 1);
      const now = this.$now();
      const maxDisplayDate = new Date(now.getFullYear() + 1, now.getMonth(), 1); // Display 1 year ahead
      return d > maxDisplayDate;
    },
  },
  methods: {
    getCurrentMonth() {
      const d = this.$now();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    },
    dateString(date) {
      if (!(date instanceof Date)) return "";
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    formatWeekday(date) {
      return date.toLocaleDateString("en-GB", { weekday: "short" });
    },
    openCancelModal(reservationId) {
      this.cancelReservationId = reservationId;
      this.showCancelModal = true;
    },
    onCancelConfirmed() {
      this.showCancelModal = false;
      this.fetchCalendar();
    },
    async fetchCalendar() {
      this.loading = true;
      this.error = "";
      try {
        const res = await api.get("/seats/seat-reservations/calendar/", {
          params: { month: this.month },
        });
        this.seatLabels = await this.fetchSeatLabels();
        this.reservationMap = {};
        res.data.reservations.forEach((r) => {
          this.reservationMap[`${r.date}_${r.seat_number}`] = r;
        });
      } catch (err) {
        this.error = "Failed to fetch calendar.";
      } finally {
        this.loading = false;
      }
    },
    async fetchHolidays() {
      try {
        const response = await fetchHolidays();
        // Hide/gray only full non-working holidays. Half holidays should stay visible/selectable.
        this.holidays = (Array.isArray(response.data) ? response.data : [])
          .filter((item) => item.period !== "HALF_HOLIDAY")
          .map((item) => item.date);
      } catch (error) {
        console.error("Failed to fetch holidays:", error);
      }
    },
    async fetchSeatLabels() {
      try {
        const resp = await apiFetchSeatLabels({ month: this.month });
        return resp.data.sort((a, b) => a.code.localeCompare(b.code));
      } catch (e) {
        console.error("Failed to fetch seat labels", e);
        return [];
      }
    },
    isToday(day) {
      const targetDate = new Date(day);
      const today = this.$now();
      return targetDate.toDateString() === today.toDateString();
    },
    isPast(day) {
      const targetDate = new Date(day);
      const today = this.$now();
      // 今日より前の日付
      targetDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      return targetDate < today;
    },
    isSaturday(date) {
      return date instanceof Date && date.getDay() === 6;
    },
    isSunday(date) {
      return date instanceof Date && date.getDay() === 0;
    },
    isHoliday(date) {
      const dStr = this.dateString(date);
      return this.holidays.includes(dStr);
    },
    isWeekend(date) {
      return this.isSaturday(date) || this.isSunday(date);
    },
    isSeatBookable(date, seatCode) {
      return (
        this.dateString(date) < NEW_SEAT_LAYOUT_START ||
        !NEW_LAYOUT_RETIRED_SEATS.includes(seatCode)
      );
    },
    prevMonth() {
      const [y, m] = this.month.split("-").map(Number);
      const d = new Date(y, m - 2, 1);
      const now = this.$now();
      const minDisplayDate = new Date(now.getFullYear(), now.getMonth() - 3, 1); // Display 3 months ago

      if (d < minDisplayDate) {
        return; // Do nothing if out of bounds
      }
      this.month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      this.fetchCalendar();
    },
    nextMonth() {
      const [y, m] = this.month.split("-").map(Number);
      const d = new Date(y, m, 1);
      const now = this.$now();
      const maxDisplayDate = new Date(now.getFullYear() + 1, now.getMonth(), 1); // Display 1 year ahead

      if (d > maxDisplayDate) {
        return; // Do nothing if out of bounds
      }
      this.month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      this.fetchCalendar();
    },
    openModal(date, seat) {
      const key = `${this.dateString(date)}_${seat}`;
      if (
        this.hideWeekends &&
        (this.isSaturday(date) || this.isSunday(date) || this.isHoliday(date))
      )
        return;
      if (!this.isSeatBookable(date, seat)) return;

      const now = this.$now();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const targetDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      );

      const res = this.reservationMap[key];

      if (res) {
        this.openCancelModal(res.id);
        return;
      }

      // Prevent creating new reservations in the past.
      if (targetDate < today) {
        return;
      }

      this.modalDate = this.dateString(date);
      this.modalSeat = { id: seat };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async afterConfirm() {
      this.closeModal();
      // Re-call fetchCalendar()/fetchReservations() like in My Page
      await this.fetchCalendar();
    },
  },
  watch: {
    month() {
      this.fetchCalendar();
    },
  },
  async mounted() {
    // Fetch holidays first, then calendar
    await this.fetchHolidays();
    this.fetchCalendar();
  },
};
</script>

<style scoped>
    .month-nav {
        margin-bottom: 1rem;
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    .seatCalendar {
        width: 100%;
        border-collapse: collapse;
    }
    thead {
        z-index: 1;
        position: sticky;
        top: 0;
    }
    th {
        text-align: center;
        color: white;
        font-size: 13px;
        height: 2rem;
        min-width: 50px;
        padding: 8px;
        &.bgBlu {
            background: var(--blue);
            &:first-child { border-top-left-radius: 10px;}
            &:last-child { border-top-right-radius: 10px;}
        }
        &.bgGre {
            background: var(--green);
            &:last-child {
                border-top-right-radius: 10px;
            }
        }
    }
    td {
        padding: 4px;
        border: 1px dashed #0757a280;
        text-align: center;
        & input,
        select {
            border: none;
        }
        &:first-child {
            border-left: none;
        }
        &:last-child {
            border-right: none;
        }
    }

    tr {
        height:3rem;
        &:first-child > td {
            border-top: none;
        }
        & > td:first-child {
            border-left: none;
            color: var(--blue);
            font-weight: 600;
            text-align: left;
        }
        &.today {
            background: var(--gradYel);
            & input,
            & select {
                background: var(--lightBlue);
            }
        }
        &.saturday,&.sunday,&.uk-holiday {
            background: #e9e9e9;
        }
    }

    tr.past {
        color: gray;
        .seat-reserved {
            background: #bababa;
        }
        &.saturday,&.sunday,&.uk-holiday {
            background: #e9e9e9;
        }
    }

    .error {
        color: red;
        margin-top: 1rem;
    }
    td.unavailable {
        background: #e0e0e0;
        cursor: not-allowed;
    }
    th:first-child { font-size: 12px;}
    .weekday {
        font-size: 12px;
        margin-left: 0.3em;
        color: var(--blue);
    }
    .seat-reserved {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 8px;
        width: calc(100% - 16px);
        height: 100%;
        border-radius: 5px;
        color: white;
        position: static;
        margin: 0 auto;
    }
    tr:not(.past){
        .seat-reserved.mine { background: var(--grad90);}
        .seat-reserved.guest { background: #3ca679;}
        .seat-reserved.other {
            background: var(--green);
            color: white;
            width: calc(100% - 20px);
        }
    }
</style>
